import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calculator,
  Package,
  DollarSign,
  Users,
  Factory,
  RefreshCw,
  Plus,
  Trash2,
  TrendingUp,
  Info,
  Layers,
  FileText,
  ClipboardList,
  Edit,
  Save,
  Percent,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostCalculator = () => {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [loadingWO, setLoadingWO] = useState(false);
  
  // Work Orders for selection
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState("");
  const [woData, setWoData] = useState(null);
  
  // Raw materials from inventory
  const [rawMaterials, setRawMaterials] = useState([]);
  
  // Selected materials for cost calculation
  const [selectedMaterials, setSelectedMaterials] = useState([
    { id: 1, material: "", gsm: 0, weight: 0, price: 0, process: "", role: "" }
  ]);
  
  // Box specifications
  const [boxSpecs, setBoxSpecs] = useState({
    length: "",
    breadth: "",
    height: "",
    ply: "3",
    quantity: 100,
  });
  
  // Labor costs
  const [laborCosts, setLaborCosts] = useState({
    hourlyRate: 150,
    hoursPerBox: 0.1,
    workers: 2,
    totalLaborCost: 0,
  });
  
  // Dynamic overhead cost fields (percentage-based)
  const [overheadFields, setOverheadFields] = useState([]);
  const [savingOverhead, setSavingOverhead] = useState(false);
  
  // Profit margin
  const [profitMargin, setProfitMargin] = useState(20);
  
  // Calculated costs
  const [costBreakdown, setCostBreakdown] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [inventoryRes, workOrdersRes, overheadRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/overhead-cost-fields`),
      ]);
      
      const allMaterials = [...inventoryRes.data];
      const uniqueMaterials = allMaterials.reduce((acc, mat) => {
        const key = mat.rm_name || mat.product || mat.reel_id;
        if (!acc.find(m => (m.rm_name || m.product || m.reel_id) === key)) {
          acc.push(mat);
        }
        return acc;
      }, []);
      
      setRawMaterials(uniqueMaterials);
      setWorkOrders(workOrdersRes.data || []);
      setOverheadFields(overheadRes.data?.map((f, i) => ({ ...f, id: Date.now() + i })) || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch work order data when selected
  const fetchWorkOrderData = async (woNumber) => {
    if (!woNumber) {
      setWoData(null);
      return;
    }
    
    try {
      setLoadingWO(true);
      const response = await axios.get(`${API}/cost-calculator/work-order/${woNumber}`);
      const data = response.data;
      setWoData(data);
      
      if (data.header) {
        setBoxSpecs(prev => ({
          ...prev,
          length: data.header.length || prev.length,
          breadth: data.header.breadth || prev.breadth,
          height: data.header.height || prev.height,
          quantity: data.header.required_quantity || prev.quantity,
        }));
      }
      
      // Build materials with process-wise usage info
      if (data.raw_materials?.items?.length > 0) {
        const processUsage = data.raw_materials.process_usage || [];
        
        const materialsFromWO = data.raw_materials.items.map((item, idx) => {
          // Find process usage for this reel
          const usage = processUsage.filter(pu => pu.reel_id === item.reel_id);
          const processInfo = usage.length > 0
            ? usage.map(u => `${u.process} (${u.role}: ${u.used_qty} ${u.unit})`).join(", ")
            : "";
          
          return {
            id: Date.now() + idx,
            material: item.reel_id || item.material_name,
            materialName: item.material_name,
            reelId: item.reel_id,
            isBitReel: item.is_bit_reel || false,
            parentReelId: item.parent_reel_id || null,
            gsm: 0,
            weight: item.weight,
            price: item.price_per_kg,
            process: processInfo,
            usageDetails: usage,
          };
        });
        setSelectedMaterials(materialsFromWO);
      }
      
      if (data.labor_costs) {
        setLaborCosts(prev => ({
          ...prev,
          workers: data.labor_costs.worker_count || prev.workers,
          totalLaborCost: data.labor_costs.total_cost || 0,
        }));
      }
      
      toast.success(`Loaded data for ${woNumber}`);
    } catch (error) {
      console.error("Error fetching work order data:", error);
      toast.error("Failed to load work order data");
    } finally {
      setLoadingWO(false);
    }
  };

  const handleWorkOrderSelect = (woNumber) => {
    setSelectedWorkOrder(woNumber);
    fetchWorkOrderData(woNumber);
  };

  const addMaterialRow = () => {
    setSelectedMaterials([
      ...selectedMaterials,
      { id: Date.now(), material: "", gsm: 0, weight: 0, price: 0, process: "", role: "" }
    ]);
  };

  const removeMaterialRow = (id) => {
    if (selectedMaterials.length > 1) {
      setSelectedMaterials(selectedMaterials.filter(m => m.id !== id));
    }
  };

  const updateMaterial = (id, field, value) => {
    setSelectedMaterials(selectedMaterials.map(m => {
      if (m.id === id) {
        if (field === "material") {
          const selected = rawMaterials.find(rm => 
            (rm.rm_name || rm.product || rm.reel_id) === value
          );
          return {
            ...m,
            material: value,
            price: selected?.paper_price || 0,
            gsm: selected?.page_no || 0,
          };
        }
        return { ...m, [field]: value };
      }
      return m;
    }));
  };

  // Overhead field CRUD
  const addOverheadField = () => {
    setOverheadFields([...overheadFields, { id: Date.now(), name: "", percentage: 0, order: overheadFields.length + 1 }]);
  };

  const updateOverheadField = (id, field, value) => {
    setOverheadFields(overheadFields.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const removeOverheadField = (id) => {
    setOverheadFields(overheadFields.filter(f => f.id !== id));
  };

  const saveOverheadFields = async () => {
    setSavingOverhead(true);
    try {
      await axios.post(`${API}/overhead-cost-fields`, {
        fields: overheadFields.map(f => ({ name: f.name, percentage: parseFloat(f.percentage) || 0 }))
      });
      toast.success("Overhead cost fields saved for future use");
    } catch (err) {
      toast.error("Failed to save overhead fields");
    } finally {
      setSavingOverhead(false);
    }
  };

  // Calculate all costs
  const calculateCosts = () => {
    setCalculating(true);
    
    try {
      const qty = parseInt(boxSpecs.quantity) || 1;
      
      // 1. Raw Material Cost
      let totalMaterialCost = 0;
      selectedMaterials.forEach(mat => {
        const weight = parseFloat(mat.weight) || 0;
        const price = parseFloat(mat.price) || 0;
        totalMaterialCost += weight * price;
      });
      const materialCostPerBox = totalMaterialCost / qty;
      
      // 2. Labor Cost
      let totalLaborCost;
      let laborCostPerBox;
      
      if (laborCosts.totalLaborCost > 0) {
        totalLaborCost = laborCosts.totalLaborCost;
        laborCostPerBox = totalLaborCost / qty;
      } else {
        laborCostPerBox = (laborCosts.hourlyRate * laborCosts.hoursPerBox * laborCosts.workers);
        totalLaborCost = laborCostPerBox * qty;
      }
      
      // 3. Overhead Costs (percentage of total material cost)
      let totalOverheadCost = 0;
      const overheadBreakdown = overheadFields.map(field => {
        const pct = parseFloat(field.percentage) || 0;
        const amount = totalMaterialCost * (pct / 100);
        totalOverheadCost += amount;
        return {
          name: field.name,
          percentage: pct,
          amount: amount,
          amountPerBox: amount / qty,
        };
      });
      const overheadPerBox = totalOverheadCost / qty;
      
      // 4. Total Cost
      const totalCostPerBox = materialCostPerBox + laborCostPerBox + overheadPerBox;
      const totalProductionCost = totalCostPerBox * qty;
      
      // 5. Profit & Selling Price
      const profitPerBox = totalCostPerBox * (profitMargin / 100);
      const sellingPricePerBox = totalCostPerBox + profitPerBox;
      const totalSellingPrice = sellingPricePerBox * qty;
      const totalProfit = profitPerBox * qty;
      
      setCostBreakdown({
        quantity: qty,
        workOrderNumber: selectedWorkOrder || null,
        materials: {
          total: totalMaterialCost,
          perBox: materialCostPerBox,
          percentage: totalCostPerBox > 0 ? ((materialCostPerBox / totalCostPerBox) * 100).toFixed(1) : "0",
        },
        labor: {
          total: totalLaborCost,
          perBox: laborCostPerBox,
          percentage: totalCostPerBox > 0 ? ((laborCostPerBox / totalCostPerBox) * 100).toFixed(1) : "0",
          isFromWO: laborCosts.totalLaborCost > 0,
        },
        overhead: {
          total: totalOverheadCost,
          perBox: overheadPerBox,
          percentage: totalCostPerBox > 0 ? ((overheadPerBox / totalCostPerBox) * 100).toFixed(1) : "0",
          breakdown: overheadBreakdown,
        },
        totalCost: {
          total: totalProductionCost,
          perBox: totalCostPerBox,
        },
        profit: {
          total: totalProfit,
          perBox: profitPerBox,
          margin: profitMargin,
        },
        sellingPrice: {
          total: totalSellingPrice,
          perBox: sellingPricePerBox,
        },
      });
      
      toast.success("Cost calculated successfully!");
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("Error calculating costs");
    } finally {
      setCalculating(false);
    }
  };

  const clearWorkOrderSelection = () => {
    setSelectedWorkOrder("");
    setWoData(null);
    setSelectedMaterials([{ id: 1, material: "", gsm: 0, weight: 0, price: 0, process: "", role: "" }]);
    setLaborCosts({ hourlyRate: 150, hoursPerBox: 0.1, workers: 2, totalLaborCost: 0 });
    setBoxSpecs({ length: "", breadth: "", height: "", ply: "3", quantity: 100 });
    setCostBreakdown(null);
    toast.info("Selection cleared");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading calculator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <Calculator className="h-8 w-8 text-emerald-400" />
              Production Cost Calculator
            </h1>
            <p className="text-gray-400 mt-1">Calculate box production costs from Work Orders or manually</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Inputs */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Work Order Selection */}
            <Card className="bg-black/60 border-2 border-amber-500/50" data-testid="work-order-selection-card">
              <CardHeader>
                <CardTitle className="text-amber-400 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Select Work Order (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Work Order Number</Label>
                    <Select
                      value={selectedWorkOrder}
                      onValueChange={handleWorkOrderSelect}
                      data-testid="work-order-select"
                    >
                      <SelectTrigger className="bg-gray-900/50 border-gray-700 text-white mt-1">
                        <SelectValue placeholder="Select a work order..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700 max-h-60">
                        {workOrders.map((wo, idx) => {
                          const woNumber = wo.header?.work_order_number || wo.work_order_number;
                          const customerName = wo.header?.customer_name || "";
                          const itemName = wo.header?.item_name || "";
                          return (
                            <SelectItem key={idx} value={woNumber}>
                              <span className="font-medium">{woNumber}</span>
                              {customerName && <span className="text-gray-400 ml-2">{customerName}</span>}
                              {itemName && <span className="text-gray-500 ml-1">- {itemName}</span>}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    {selectedWorkOrder && (
                      <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={clearWorkOrderSelection} data-testid="clear-wo-btn">
                        Clear Selection
                      </Button>
                    )}
                    {loadingWO && (
                      <div className="flex items-center gap-2 text-amber-400">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Loading...</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {woData && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="wo-summary">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Customer:</span>
                        <p className="text-white font-medium">{woData.header?.customer_name || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Item:</span>
                        <p className="text-white font-medium">{woData.header?.item_name || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Quantity:</span>
                        <p className="text-white font-medium">{woData.header?.required_quantity || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Delivery Date:</span>
                        <p className="text-white font-medium">{woData.header?.delivery_date || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Box Specifications */}
            <Card className="bg-black/60 border border-cyan-500/30">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Box Specifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-gray-400">Length (inch)</Label>
                    <Input type="number" value={boxSpecs.length} onChange={(e) => setBoxSpecs({ ...boxSpecs, length: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="12" data-testid="box-length-input" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Breadth (inch)</Label>
                    <Input type="number" value={boxSpecs.breadth} onChange={(e) => setBoxSpecs({ ...boxSpecs, breadth: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="8" data-testid="box-breadth-input" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Height (inch)</Label>
                    <Input type="number" value={boxSpecs.height} onChange={(e) => setBoxSpecs({ ...boxSpecs, height: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="6" data-testid="box-height-input" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Ply</Label>
                    <Select value={boxSpecs.ply} onValueChange={(val) => setBoxSpecs({ ...boxSpecs, ply: val })}>
                      <SelectTrigger className="bg-gray-900/50 border-gray-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="3">3 Ply</SelectItem>
                        <SelectItem value="5">5 Ply</SelectItem>
                        <SelectItem value="7">7 Ply</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-400">Quantity</Label>
                    <Input type="number" value={boxSpecs.quantity} onChange={(e) => setBoxSpecs({ ...boxSpecs, quantity: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="100" data-testid="box-quantity-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Raw Materials */}
            <Card className="bg-black/60 border border-emerald-500/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-emerald-400 flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Raw Materials
                  </CardTitle>
                  {woData?.raw_materials && (
                    <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-xs">
                      {woData.raw_materials.items?.length || 0} Reels from WO
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={addMaterialRow} data-testid="add-material-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Material
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedMaterials.map((mat, idx) => (
                    <div key={mat.id} className="p-3 bg-gray-900/40 rounded-lg border border-gray-700/50" data-testid={`material-row-${idx}`}>
                      <div className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-5">
                          <Label className="text-gray-400 text-xs">
                            {mat.reelId ? "Reel ID / Material" : "Material Name"}
                          </Label>
                          {mat.reelId ? (
                            <div className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 mt-1 text-white flex items-center gap-2">
                              <span className="font-medium text-emerald-400">{mat.reelId}</span>
                              <span className="text-gray-400 text-xs">({mat.materialName})</span>
                              {mat.isBitReel && (
                                <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-xs ml-auto">
                                  Bit Reel
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <Select value={mat.material} onValueChange={(val) => updateMaterial(mat.id, "material", val)}>
                              <SelectTrigger className="bg-gray-900/50 border-gray-700 text-white mt-1">
                                <SelectValue placeholder="Select material" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-900 border-gray-700 max-h-60">
                                {rawMaterials.map((rm, i) => {
                                  const name = rm.rm_name || rm.product || rm.reel_id;
                                  return (
                                    <SelectItem key={i} value={name}>
                                      {name} - ₹{rm.paper_price}/kg
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Weight (kg)</Label>
                          <Input type="number" value={mat.weight} onChange={(e) => updateMaterial(mat.id, "weight", e.target.value)} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="0" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Price (₹/kg)</Label>
                          <Input type="number" value={mat.price} onChange={(e) => updateMaterial(mat.id, "price", e.target.value)} className="bg-gray-900/50 border-gray-700 text-white mt-1" placeholder="0" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-gray-400 text-xs">Subtotal</Label>
                          <div className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 mt-1 text-emerald-400 font-medium">
                            ₹{((parseFloat(mat.weight) || 0) * (parseFloat(mat.price) || 0)).toFixed(2)}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removeMaterialRow(mat.id)} disabled={selectedMaterials.length === 1}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Process Usage Info */}
                      {mat.process && (
                        <div className="mt-2 px-1">
                          <p className="text-xs text-cyan-400">
                            {mat.usageDetails?.map((u, i) => (
                              <span key={i} className="inline-block mr-3">
                                <span className="text-gray-400">{u.process}:</span>{" "}
                                <span className="text-white font-medium">{u.role} - {u.used_qty} {u.unit}</span>
                              </span>
                            ))}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Material Totals */}
                  <div className="pt-3 mt-3 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Material Weight:</span>
                      <span className="text-emerald-400 font-bold">
                        {selectedMaterials.reduce((sum, m) => sum + (parseFloat(m.weight) || 0), 0).toFixed(2)} kg
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-gray-400">Total Material Cost:</span>
                      <span className="text-emerald-400 font-bold text-lg">
                        ₹{selectedMaterials.reduce((sum, m) => sum + ((parseFloat(m.weight) || 0) * (parseFloat(m.price) || 0)), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Labor Costs */}
            <Card className="bg-black/60 border border-purple-500/30">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-purple-400 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Labor Costs
                  </CardTitle>
                  {laborCosts.totalLaborCost > 0 && (
                    <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-xs">
                      From WO Timesheet
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {laborCosts.totalLaborCost > 0 ? (
                  <div className="space-y-4" data-testid="labor-from-wo">
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <p className="text-gray-400 text-sm mb-2">Labor cost calculated from actual timesheet entries:</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <span className="text-gray-400 text-xs">Total Hours</span>
                          <p className="text-purple-400 font-bold text-xl">{woData?.labor_costs?.total_hours || 0} hrs</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs">Workers</span>
                          <p className="text-purple-400 font-bold text-xl">{woData?.labor_costs?.worker_count || 0}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-xs">Total Labor Cost</span>
                          <p className="text-purple-400 font-bold text-xl">₹{laborCosts.totalLaborCost.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                    {woData?.labor_costs?.details?.length > 0 && (
                      <div>
                        <table className="w-full text-sm">
                          <thead className="text-gray-400 border-b border-gray-700">
                            <tr>
                              <th className="text-left py-2">Employee</th>
                              <th className="text-left py-2">Machine</th>
                              <th className="text-left py-2">Date</th>
                              <th className="text-right py-2">Hours</th>
                              <th className="text-center py-2">OT</th>
                              <th className="text-right py-2">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-300">
                            {woData.labor_costs.details.map((detail, idx) => (
                              <tr key={idx} className="border-b border-gray-800">
                                <td className="py-1">{detail.employee_name}</td>
                                <td className="py-1">{detail.machine_name}</td>
                                <td className="py-1 text-gray-500 text-xs">{detail.date}</td>
                                <td className="py-1 text-right">{detail.hours}</td>
                                <td className="py-1 text-center">{detail.is_overtime ? <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-xs">OT</Badge> : "-"}</td>
                                <td className="py-1 text-right text-purple-400">₹{detail.cost.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4" data-testid="labor-manual">
                    <div>
                      <Label className="text-gray-400 flex items-center gap-1">
                        Hourly Rate (₹)
                        <TooltipProvider><Tooltip><TooltipTrigger><Info className="h-3 w-3 text-gray-500" /></TooltipTrigger><TooltipContent><p>Average wage per worker per hour</p></TooltipContent></Tooltip></TooltipProvider>
                      </Label>
                      <Input type="number" value={laborCosts.hourlyRate} onChange={(e) => setLaborCosts({ ...laborCosts, hourlyRate: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-400 flex items-center gap-1">
                        Hours per Box
                        <TooltipProvider><Tooltip><TooltipTrigger><Info className="h-3 w-3 text-gray-500" /></TooltipTrigger><TooltipContent><p>Time to produce one box (e.g., 0.1 = 6 minutes)</p></TooltipContent></Tooltip></TooltipProvider>
                      </Label>
                      <Input type="number" step="0.01" value={laborCosts.hoursPerBox} onChange={(e) => setLaborCosts({ ...laborCosts, hoursPerBox: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-400">No. of Workers</Label>
                      <Input type="number" value={laborCosts.workers} onChange={(e) => setLaborCosts({ ...laborCosts, workers: e.target.value })} className="bg-gray-900/50 border-gray-700 text-white mt-1" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Overhead Costs - Dynamic Percentage-Based */}
            <Card className="bg-black/60 border border-amber-500/30" data-testid="overhead-cost-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-amber-400 flex items-center gap-2">
                    <Factory className="h-5 w-5" />
                    Overhead Costs
                  </CardTitle>
                  <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                    <Percent className="h-3 w-3" />
                    Percentage of Total Material Cost
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" onClick={addOverheadField} data-testid="add-overhead-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                  <Button size="sm" onClick={saveOverheadFields} disabled={savingOverhead} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="save-overhead-btn">
                    <Save className="h-4 w-4 mr-1" />
                    {savingOverhead ? "Saving..." : "Save"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overheadFields.map((field, idx) => {
                    const totalMat = selectedMaterials.reduce((sum, m) => sum + ((parseFloat(m.weight) || 0) * (parseFloat(m.price) || 0)), 0);
                    const amount = totalMat * ((parseFloat(field.percentage) || 0) / 100);
                    return (
                      <div key={field.id} className="grid grid-cols-12 gap-3 items-center" data-testid={`overhead-row-${idx}`}>
                        <div className="col-span-4">
                          <Input
                            value={field.name}
                            onChange={(e) => updateOverheadField(field.id, "name", e.target.value)}
                            className="bg-gray-900/50 border-gray-700 text-white"
                            placeholder="e.g., Electricity"
                            data-testid={`overhead-name-${idx}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              step="0.1"
                              value={field.percentage}
                              onChange={(e) => updateOverheadField(field.id, "percentage", e.target.value)}
                              className="bg-gray-900/50 border-gray-700 text-white"
                              data-testid={`overhead-pct-${idx}`}
                            />
                            <span className="text-gray-400 text-sm">%</span>
                          </div>
                        </div>
                        <div className="col-span-4">
                          <div className="bg-gray-800/50 border border-gray-700 rounded-md px-3 py-2 text-amber-400 font-medium text-sm">
                            ₹{amount.toFixed(2)}
                          </div>
                        </div>
                        <div className="col-span-2 flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8" onClick={() => removeOverheadField(field.id)} data-testid={`overhead-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {overheadFields.length === 0 && (
                    <p className="text-gray-500 text-center py-4 text-sm">No overhead fields. Click "Add Field" to add one.</p>
                  )}
                  
                  {/* Overhead Total */}
                  {overheadFields.length > 0 && (
                    <div className="pt-3 mt-2 border-t border-gray-700">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Total Overhead ({overheadFields.reduce((s, f) => s + (parseFloat(f.percentage) || 0), 0).toFixed(1)}%)</span>
                        <span className="text-amber-400 font-bold">
                          ₹{(() => {
                            const totalMat = selectedMaterials.reduce((sum, m) => sum + ((parseFloat(m.weight) || 0) * (parseFloat(m.price) || 0)), 0);
                            return overheadFields.reduce((sum, f) => sum + totalMat * ((parseFloat(f.percentage) || 0) / 100), 0).toFixed(2);
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Profit Margin & Calculate */}
            <Card className="bg-black/60 border border-cyan-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <Label className="text-gray-400">Profit Margin (%)</Label>
                      <Input type="number" value={profitMargin} onChange={(e) => setProfitMargin(e.target.value)} className="bg-gray-900/50 border-gray-700 text-white mt-1 w-32" data-testid="profit-margin-input" />
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-black font-bold px-8"
                    onClick={calculateCosts}
                    disabled={calculating}
                    data-testid="calculate-cost-btn"
                  >
                    {calculating ? <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> : <Calculator className="h-5 w-5 mr-2" />}
                    Calculate Cost
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            {costBreakdown ? (
              <>
                {costBreakdown.workOrderNumber && (
                  <Card className="bg-black/60 border border-amber-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-amber-400" />
                        <span className="text-gray-400 text-sm">Work Order:</span>
                        <span className="text-amber-400 font-bold">{costBreakdown.workOrderNumber}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Total Selling Price */}
                <Card className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-500/50" data-testid="selling-price-card">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400 mb-1">Recommended Selling Price</p>
                    <p className="text-5xl font-black text-emerald-400">
                      ₹{costBreakdown.sellingPrice.perBox.toFixed(2)}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">per box</p>
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <p className="text-gray-400 text-sm">Total for {costBreakdown.quantity} boxes</p>
                      <p className="text-2xl font-bold text-white">
                        ₹{costBreakdown.sellingPrice.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Breakdown */}
                <Card className="bg-black/60 border border-gray-700" data-testid="cost-breakdown-card">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-cyan-400" />
                      Cost Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Materials */}
                    <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-emerald-400" />
                        <span className="text-gray-300">Raw Materials</span>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">₹{costBreakdown.materials.total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{costBreakdown.materials.percentage}% of cost</p>
                      </div>
                    </div>

                    {/* Labor */}
                    <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-400" />
                        <span className="text-gray-300">Labor</span>
                        {costBreakdown.labor.isFromWO && (
                          <Badge variant="outline" className="border-purple-500/30 text-purple-400 text-xs">Actual</Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-purple-400 font-bold">₹{costBreakdown.labor.total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{costBreakdown.labor.percentage}% of cost</p>
                      </div>
                    </div>

                    {/* Overhead */}
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Factory className="h-4 w-4 text-amber-400" />
                          <span className="text-gray-300">Overhead</span>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 font-bold">₹{costBreakdown.overhead.total.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{costBreakdown.overhead.percentage}% of cost</p>
                        </div>
                      </div>
                      {/* Overhead breakdown */}
                      {costBreakdown.overhead.breakdown?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-500/20 space-y-1">
                          {costBreakdown.overhead.breakdown.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-gray-400">{item.name} ({item.percentage}%)</span>
                              <span className="text-amber-300">₹{item.amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Total Cost */}
                    <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-600">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-white" />
                        <span className="text-white font-medium">Total Cost</span>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">₹{costBreakdown.totalCost.total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">₹{costBreakdown.totalCost.perBox.toFixed(2)} / box</p>
                      </div>
                    </div>

                    {/* Profit */}
                    <div className="flex items-center justify-between p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-cyan-400" />
                        <span className="text-gray-300">Profit ({costBreakdown.profit.margin}%)</span>
                      </div>
                      <div className="text-right">
                        <p className="text-cyan-400 font-bold">₹{costBreakdown.profit.total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">₹{costBreakdown.profit.perBox.toFixed(2)} / box</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Card */}
                <Card className="bg-black/60 border border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Quantity</span>
                      <span className="text-white">{costBreakdown.quantity} boxes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Production Cost</span>
                      <span className="text-white">₹{costBreakdown.totalCost.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Profit</span>
                      <span className="text-emerald-400">₹{costBreakdown.profit.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between font-bold">
                        <span className="text-gray-300">Total Selling Price</span>
                        <span className="text-emerald-400">₹{costBreakdown.sellingPrice.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-black/60 border border-gray-700">
                <CardContent className="p-12 text-center">
                  <Calculator className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-400 mb-2">Enter Details & Calculate</h3>
                  <p className="text-gray-500 text-sm">
                    Select a work order to auto-fill data, or manually enter box specifications, materials, labor and overhead costs, then click "Calculate Cost" to see the breakdown.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostCalculator;
